/* eslint-disable react/prop-types */
import { FC, PropsWithChildren, useCallback } from 'react'
import { Button } from '../../../shadcn'
import { Kbd, KbdKey } from '../../../ui/kbd'

export const isMacOS = (): boolean => typeof window !== 'undefined' && window.navigator.platform === 'MacIntel'

type ShortcutKeyResult = {
  symbol: string
  readable: string
}

const shortcutKeyMap: Record<string, ShortcutKeyResult> = {
  mod: isMacOS() ? { symbol: '⌘', readable: 'Command' } : { symbol: 'Ctrl', readable: 'Control' },
  alt: isMacOS() ? { symbol: '⌥', readable: 'Option' } : { symbol: 'Alt', readable: 'Alt' },
  shift: { symbol: '⇧', readable: 'Shift' },
}

export const getShortcutKey = (key: string): ShortcutKeyResult =>
  shortcutKeyMap[key.toLowerCase()] || { symbol: key, readable: key }

export const getShortcutKeys = (keys: string[]): ShortcutKeyResult[] => keys.map(getShortcutKey)

export type EditorFooterProps = {
  handleSave: () => void
  disabled?: boolean
  children?: React.ReactNode
}

export const EditorFooter: FC<EditorFooterProps> = ({ handleSave, disabled, children }) => {
  // add hotkey to save
  const handleSaveWithHotkey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' && e.metaKey) {
      handleSave()
    }
  }

  return (
    <div className="flex justify-end items-center p-2 w-full border-t bg-background">
      {children}
      <Button onKeyDown={handleSaveWithHotkey} type="button" size="sm" onClick={handleSave} disabled={disabled}>
        <Kbd className="bg-background-muted text-foreground-muted border-muted/30 text-[8px] mr-2">
          <KbdKey>⌘</KbdKey>
          <KbdKey>Enter</KbdKey>
        </Kbd>
        Save
      </Button>
    </div>
  )
}

export type BlockKeysProps = PropsWithChildren<{
  onEscape: () => void
  blockedKeys?: string[]
}>

export const BlockKeys: React.FC<BlockKeysProps> = ({ onEscape, blockedKeys = [], children }) => {
  const blockedKeySet = new Set(blockedKeys)

  const handleKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLDivElement>) => {
      if (blockedKeySet.has(ev.key) && !ev.metaKey) {
        ev.stopPropagation()
      }
    },
    [onEscape, blockedKeySet]
  )

  return <div onKeyDown={handleKeyDown}>{children}</div>
}
